import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Select,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import {
  Add16Regular,
  Dismiss16Regular,
  ErrorCircle24Regular,
  Search16Regular,
} from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useTemplates, type UseTemplatesOptions } from '@/hooks/useTemplates';
import { TablePaginationFooter, usePagination } from '@/components/shared/DataTable';
import { telemetryService } from '@/services/telemetryService';
import { ImportTemplateCard } from './ImportTemplateCard';
import { TemplateCard } from './TemplateCard';
import { TemplateGalleryDialog } from './TemplateGalleryDialog';

type SortByOption = 'featured' | 'name-asc' | 'name-desc';

interface DialogEntry {
  mode: 'preview' | 'import';
  templateId?: string;
}

export interface InlineTemplateGalleryProps {
  headingLevel?: 1 | 2;
  headingRef?: React.Ref<HTMLHeadingElement>;
  onStartFromScratch: () => void;
  onCreateFromTemplate: (templateId: string, workflowName: string) => void;
  defaultWorkflowName: string;
  existingWorkflowNames: string[];
  isCreating: boolean;
}

const SORT_OPTIONS: { value: SortByOption; label: string }[] = [
  { value: 'featured', label: 'Featured' },
  { value: 'name-asc', label: 'Name (A-Z)' },
  { value: 'name-desc', label: 'Name (Z-A)' },
];

const useStyles = makeStyles({
  root: {
    width: '100%',
    maxWidth: '1000px',
    marginLeft: 'auto',
    marginRight: 'auto',
    alignSelf: 'center',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  headingGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  heading: {
    margin: 0,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  subtitle: {
    color: tokens.colorNeutralForeground2,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  search: {
    flex: '1 1 260px',
    minWidth: 0,
  },
  select: {
    flex: '0 1 160px',
    minWidth: '130px',
  },
  scratchButton: {
    marginLeft: 'auto',
  },
  library: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: tokens.spacingHorizontalM,
    '@media (min-width: 421px) and (max-width: 680px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (min-width: 681px) and (max-width: 900px)': {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    '@media (min-width: 901px)': {
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    },
  },
  state: {
    minHeight: '120px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    gridColumn: '1 / -1',
  },
  stateIcon: {
    color: tokens.colorNeutralForeground3,
  },
  liveRegion: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: tokens.spacingVerticalXS,
    '& > div': {
      justifyContent: 'flex-end',
    },
  },
});

export const InlineTemplateGallery: React.FC<InlineTemplateGalleryProps> = ({
  headingLevel = 2,
  headingRef,
  onStartFromScratch,
  onCreateFromTemplate,
  defaultWorkflowName,
  existingWorkflowNames,
  isCreating,
}) => {
  const styles = useStyles();
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState<SortByOption>('featured');
  const [dialogEntry, setDialogEntry] = useState<DialogEntry | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    telemetryService.trackEvent('template.inline_library_opened');
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const options = useMemo<UseTemplatesOptions>(() => {
    const next: UseTemplatesOptions = { sortBy };
    if (selectedCategory) next.category = selectedCategory;
    if (searchQuery) next.searchQuery = searchQuery;
    return next;
  }, [searchQuery, selectedCategory, sortBy]);

  const { templates, categories, isLoading, error } = useTemplates(options);
  const displayedTemplates = useMemo(() => {
    if (sortBy === 'featured') return templates;
    const direction = sortBy === 'name-asc' ? 1 : -1;
    return [...templates].sort(
      (left, right) => direction * left.name.localeCompare(right.name),
    );
  }, [sortBy, templates]);
  const pagination = usePagination<(typeof displayedTemplates)[number]>({
    totalItems: displayedTemplates.length,
    defaultPageSize: 11,
    resetDeps: [searchQuery, selectedCategory, sortBy],
  });
  const pagedTemplates = useMemo(
    () => pagination.paginate(displayedTemplates),
    [displayedTemplates, pagination],
  );

  const rememberTrigger = useCallback(() => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, []);

  const handleTemplateClick = useCallback((templateId: string) => {
    rememberTrigger();
    telemetryService.trackEvent('template.selected', {
      source: 'inline-library',
      templateId,
    });
    setDialogEntry({ mode: 'preview', templateId });
  }, [rememberTrigger]);

  const handleImportClick = useCallback(() => {
    rememberTrigger();
    telemetryService.trackEvent('template.import_opened', { source: 'inline-library' });
    setDialogEntry({ mode: 'import' });
  }, [rememberTrigger]);

  const handleDialogDismiss = useCallback(() => {
    setDialogEntry(null);
    const trigger = returnFocusRef.current;
    window.requestAnimationFrame(() => trigger?.focus());
  }, []);

  const handleSearchChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
      setSearchInput(data.value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSearchQuery(data.value);
        telemetryService.trackEvent(
          'template.inline_search_changed',
          { hasQuery: String(data.value.trim().length > 0) },
          { queryLength: data.value.trim().length },
        );
      }, 300);
    },
    [],
  );

  const clearSearch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchInput('');
    setSearchQuery('');
    telemetryService.trackEvent('template.inline_search_cleared');
  }, []);

  const clearFilters = useCallback(() => {
    clearSearch();
    setSelectedCategory('');
  }, [clearSearch]);

  const handleCategoryChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const category = event.target.value === 'all' ? '' : event.target.value;
    setSelectedCategory(category);
    telemetryService.trackEvent('template.inline_category_changed', {
      category: category || 'all',
    });
  }, []);

  const handleSortChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const sort = event.target.value as SortByOption;
    setSortBy(sort);
    telemetryService.trackEvent('template.inline_sort_changed', { sort });
  }, []);

  const handlePageChange = useCallback((page: number) => {
    pagination.setPage(page);
    telemetryService.trackEvent('template.inline_page_changed', {}, { page });
  }, [pagination]);

  const handleRetry = useCallback(() => {
    telemetryService.trackEvent('template.inline_retry');
    void queryClient.invalidateQueries({ queryKey: ['templates'] });
  }, [queryClient]);

  const handleScratch = useCallback(() => {
    telemetryService.trackEvent('template.inline_start_from_scratch');
    onStartFromScratch();
  }, [onStartFromScratch]);

  const hasFilters = searchQuery.length > 0 || selectedCategory.length > 0;
  const resultAnnouncement = !isLoading && !error && hasFilters
    ? templates.length === 0
      ? 'No templates found. Try adjusting your search or filters.'
      : `${templates.length} ${templates.length === 1 ? 'template' : 'templates'} found.`
    : '';

  return (
    <section className={styles.root} aria-labelledby="inline-template-library-heading">
      <span
        className={styles.liveRegion}
        role="status"
        aria-label="Template search results"
        aria-live="polite"
        aria-atomic="true"
      >
        {resultAnnouncement}
      </span>
      <div className={styles.headingGroup}>
        <Heading
          ref={headingRef}
          tabIndex={-1}
          id="inline-template-library-heading"
          className={styles.heading}
        >
          Template library
        </Heading>
        <Text className={styles.subtitle}>
          Start with a proven workflow, import a JSON template, or begin with a blank canvas.
        </Text>
      </div>

      <div className={styles.controls}>
        <Input
          className={styles.search}
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search templates"
          aria-label="Search templates"
          contentBefore={<Search16Regular aria-hidden="true" />}
          contentAfter={
            searchInput ? (
              <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss16Regular />}
                aria-label="Clear search"
                onClick={clearSearch}
              />
            ) : null
          }
        />
        <Select
          className={styles.select}
          value={selectedCategory || 'all'}
          onChange={handleCategoryChange}
          aria-label="Filter by category"
        >
          {categories.map((category) => (
            <option key={category.key} value={category.key}>
              {category.label}
            </option>
          ))}
        </Select>
        <Select
          className={styles.select}
          value={sortBy}
          onChange={handleSortChange}
          aria-label="Sort templates"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Button
          className={styles.scratchButton}
          appearance="outline"
          icon={<Add16Regular />}
          onClick={handleScratch}
        >
          Start from scratch
        </Button>
      </div>

      <div className={styles.library}>
        <div className={styles.grid}>
          <ImportTemplateCard onClick={handleImportClick} />
          {pagedTemplates.map((template) => (
            <TemplateCard key={template.id} template={template} onClick={handleTemplateClick} />
          ))}
          {isLoading && templates.length === 0 ? (
            <div className={styles.state} role="status" aria-live="polite">
              <Spinner size="small" label="Loading templates…" />
            </div>
          ) : error && templates.length === 0 ? (
            <div className={styles.state} role="alert">
              <ErrorCircle24Regular className={styles.stateIcon} aria-hidden="true" />
              <Text weight="semibold">Couldn't load templates</Text>
              <Text size={200}>Check your network connection and try again.</Text>
              <Button size="small" onClick={handleRetry}>Retry</Button>
            </div>
          ) : templates.length === 0 ? (
            <div className={styles.state}>
              <Search16Regular className={styles.stateIcon} aria-hidden="true" />
              <Text weight="semibold">No templates found</Text>
              <Text size={200}>
                {hasFilters ? 'Try adjusting your search or filters.' : 'No templates are available.'}
              </Text>
              {hasFilters ? <Button size="small" onClick={clearFilters}>Clear filters</Button> : null}
            </div>
          ) : null}
        </div>

        <div className={styles.footer}>
          <TablePaginationFooter
            totalItems={templates.length}
            currentPage={pagination.page}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {dialogEntry ? (
        <TemplateGalleryDialog
          open
          entryMode={dialogEntry.mode}
          {...(dialogEntry.templateId
            ? { initialPreviewTemplateId: dialogEntry.templateId }
            : {})}
          onDismiss={handleDialogDismiss}
          requireWorkflowName
          defaultWorkflowName={defaultWorkflowName}
          existingWorkflowNames={existingWorkflowNames}
          isCreating={isCreating}
          onCreateFromTemplate={onCreateFromTemplate}
        />
      ) : null}
    </section>
  );
};

InlineTemplateGallery.displayName = 'InlineTemplateGallery';
